import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { ButtonLink } from "design-system/atoms/Button"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { formatNumber, formatPercent } from "lopermut"
import { ArrowUpRight } from "lucide-react"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SegmentGroupResponseDataSchema, SegmentResponseDataSchema } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<Features, z.infer<typeof SegmentResponseDataSchema>>()

type Props = {
  segments: z.infer<typeof SegmentResponseDataSchema>[]
  segmentGroup: z.infer<typeof SegmentGroupResponseDataSchema>
  isPending: boolean
  totalCustomers: number
}

export function SegmentsTable({ segments, segmentGroup, isPending, totalCustomers }: Props) {
  const { t } = useTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("segments.table.name"),
          cell: (info) => getSegmentTranslation(info.getValue(), segmentGroup),
        }),
        columnHelper.accessor("count", {
          header: t("segments.table.customers"),
          cell: (info) => (
            <div className="text-right">{formatNumber({ amount: info.getValue(), fix: 0 })}</div>
          ),
          meta: { align: "right" },
        }),
        columnHelper.accessor("count", {
          id: "percentage",
          header: t("segments.table.percentage"),
          cell: (info) => (
            <div className="text-right">
              {formatPercent({
                amount: (info.getValue() / totalCustomers) * 100,
                fix: 0,
              })}
            </div>
          ),
          meta: { align: "right" },
        }),
        columnHelper.accessor("updatedAt", {
          header: t("segments.table.lastUpdate"),
          cell: (info) => <DateDisplay date={info.getValue()} />,
          meta: { align: "right" },
        }),
        columnHelper.display({
          id: "link",
          cell: (row) => (
            <ButtonLink
              to="/customers/list"
              search={{ filterBy: { segmentIds: [row.row.original.id] } }}
              variant="outline"
            >
              <span>{t("segments.table.view")}</span>
              <ArrowUpRight className="size-4" />
            </ButtonLink>
          ),
          meta: { align: "right", className: "w-52" },
        }),
      ]),
    [getSegmentTranslation, segmentGroup, t, totalCustomers],
  )

  const table = useTable({
    data: segments ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || segments === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (segments.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("segments.list.empty.title")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
